import {Layout} from '../../src/Layout';
export default Layout;

import docs from 'docs:@react-spectrum/s2';

export const tags = ['toolbar'];
export const description = 'Used when a user needs to perform actions on one or more items at the same time.';

# ActionBar

<PageDescription>{docs.exports.ActionBar.description}</PageDescription>

<ExampleSwitcher type={null} examples={['TableView', 'ListView', 'TreeView']}>

```tsx render docs={docs.exports.ActionBar} links={docs.links} props={['isEmphasized']} type="s2" wide
"use client";
import {ActionBar, ActionButton, TableView, TableHeader, TableBody, Column, Row, Cell} from '@react-spectrum/s2';
import Edit from '@react-spectrum/s2/icons/Edit';
import Copy from '@react-spectrum/s2/icons/Copy';
import Delete from '@react-spectrum/s2/icons/Delete';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

let rows = [
  {id: 1, name: 'Charizard', type: 'Fire, Flying', level: '67'},
  {id: 2, name: 'Blastoise', type: 'Water', level: '56'},
  {id: 3, name: 'Venusaur', type: 'Grass, Poison', level: '83'},
  {id: 4, name: 'Pikachu', type: 'Electric', level: '100'}
];

function Example(props) {
  return (
    <TableView
      aria-label="Table with action bar"
      selectionMode="multiple"
      defaultSelectedKeys={[2]}
      styles={style({width: 'full', height: 250})}
      renderActionBar={(selectedKeys) => (
        /*- begin focus -*/
        <ActionBar {...props}/* PROPS */>
          <ActionButton aria-label="Edit" onPress={() => alert('Edit action')}>
            <Edit />
          </ActionButton>
          <ActionButton aria-label="Copy" onPress={() => alert('Copy action')}>
            <Copy />
          </ActionButton>
          <ActionButton aria-label="Delete" onPress={() => alert('Delete action')}>
            <Delete />
          </ActionButton>
        </ActionBar>
        /*- end focus -*/
      )}>
      <TableHeader>
        <Column key="name" isRowHeader>Name</Column>
        <Column key="type">Type</Column>
        <Column key="level">Level</Column>
      </TableHeader>
      <TableBody items={rows}>
        {item => (
          <Row>
            <Cell key="name">{item.name}</Cell>
            <Cell key="type">{item.type}</Cell>
            <Cell key="level">{item.level}</Cell>
          </Row>
        )}
      </TableBody>
    </TableView>
  );
}
```

```tsx render docs={docs.exports.ActionBar} links={docs.links} props={['isEmphasized']} type="s2" wide
"use client";
import {ActionBar, ActionButton, ListView, ListViewItem, Text} from '@react-spectrum/s2';
import Edit from '@react-spectrum/s2/icons/Edit';
import Copy from '@react-spectrum/s2/icons/Copy';
import Delete from '@react-spectrum/s2/icons/Delete';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

let items = [
  {id: 1, name: 'Charizard', type: 'Fire, Flying', level: '67'},
  {id: 2, name: 'Blastoise', type: 'Water', level: '56'},
  {id: 3, name: 'Venusaur', type: 'Grass, Poison', level: '83'},
  {id: 4, name: 'Pikachu', type: 'Electric', level: '100'}
];

function Example(props) {
  return (
    <ListView
      aria-label="List with action bar"
      selectionMode="multiple"
      defaultSelectedKeys={[2]}
      styles={style({width: 'full', height: 250})}
      items={items}
      renderActionBar={(selectedKeys) => (
        /*- begin focus -*/
        <ActionBar {...props}/* PROPS */>
          <ActionButton aria-label="Edit" onPress={() => alert('Edit action')}>
            <Edit />
          </ActionButton>
          <ActionButton aria-label="Copy" onPress={() => alert('Copy action')}>
            <Copy />
          </ActionButton>
          <ActionButton aria-label="Delete" onPress={() => alert('Delete action')}>
            <Delete />
          </ActionButton>
        </ActionBar>
        /*- end focus -*/
      )}>
      {item => (
        <ListViewItem id={item.id} textValue={item.name}>
          <Text>{item.name}</Text>
          <Text slot="description">{item.type} • Level {item.level}</Text>
        </ListViewItem>
      )}
    </ListView>
  );
}
```

```tsx render docs={docs.exports.ActionBar} links={docs.links} props={['isEmphasized']} type="s2" wide
"use client";
import {ActionBar, ActionButton, TreeView, TreeViewItem, TreeViewItemContent} from '@react-spectrum/s2';
import Edit from '@react-spectrum/s2/icons/Edit';
import Copy from '@react-spectrum/s2/icons/Copy';
import Delete from '@react-spectrum/s2/icons/Delete';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

function Example(props) {
  return (
    <TreeView
      aria-label="TreeView with action bar"
      selectionMode="multiple"
      defaultExpandedKeys={['documents', 'project', 'photos']}
      defaultSelectedKeys={['budget']}
      styles={style({width: 'full', height: 250, maxWidth: 400})}
      renderActionBar={(selectedKeys) => (
        /*- begin focus -*/
        <ActionBar {...props}/* PROPS */>
          <ActionButton aria-label="Edit" onPress={() => alert('Edit action')}>
            <Edit />
          </ActionButton>
          <ActionButton aria-label="Copy" onPress={() => alert('Copy action')}>
            <Copy />
          </ActionButton>
          <ActionButton aria-label="Delete" onPress={() => alert('Delete action')}>
            <Delete />
          </ActionButton>
        </ActionBar>
        /*- end focus -*/
      )}>
      <TreeViewItem id="documents" textValue="Documents">
        <TreeViewItemContent>Documents</TreeViewItemContent>
        <TreeViewItem id="project" textValue="Project">
          <TreeViewItemContent>Project</TreeViewItemContent>
          <TreeViewItem id="report" textValue="Weekly Report">
            <TreeViewItemContent>Weekly Report</TreeViewItemContent>
          </TreeViewItem>
          <TreeViewItem id="budget" textValue="Budget">
            <TreeViewItemContent>Budget</TreeViewItemContent>
          </TreeViewItem>
        </TreeViewItem>
      </TreeViewItem>
      <TreeViewItem id="photos" textValue="Photos">
        <TreeViewItemContent>Photos</TreeViewItemContent>
        <TreeViewItem id="image-1" textValue="Image 1">
          <TreeViewItemContent>Image 1</TreeViewItemContent>
          </TreeViewItem>
        <TreeViewItem id="image-2" textValue="Image 2">
          <TreeViewItemContent>Image 2</TreeViewItemContent>
        </TreeViewItem>
      </TreeViewItem>
    </TreeView>
  );
}
```

</ExampleSwitcher>

## API

```tsx links={{ActionBar: '#actionbar', ActionButton: 'ActionButton'}}
<ActionBar>
  <ActionButton />
</ActionBar>
```

### ActionBar

<PropTable component={docs.exports.ActionBar} links={docs.links} />
